import {formatISOToBasicDateFormat} from "components/dashboard/data-input-output-table/utils";
import {ITimespan} from "components/dashboard/product-template-table/utils/cellInterfaces";
import {CellSubType} from "utils/enums";
import {MultiSelect} from "primereact/multiselect";
import React from "react";
import { TimeSpan, timeSpanToSeconds } from "utils/time-utils";

interface IMultiValueProps {
    selectionValues: SelectionValueTypeAlias[] | undefined;
    changeHandler: (value: any) => void;
    subType?: SelectionValuesType;
    multiValueSeparator?: string;
    value: any;
    isInEditMode: boolean;
}

type SelectionValuesType = "STRING" | "LONG" | "DOUBLE" | "TIME" | "TIMESPAN";

type SelectionValueTypeAlias = string | number | ITimespan;
type DropdownOptionType = {label: string, value: SelectionValueTypeAlias};

const normalizeTimespan = (timespan: ITimespan): ITimespan => {
    return {
        days: timespan.days || 0,
        hours: timespan.hours || 0,
        minutes: timespan.minutes || 0,
        seconds: timespan.seconds || 0
    };
};

const convertTimespanToString = (timespan: ITimespan) => {
    if (!timespan) {
        return "";
    }
    let timeString = "";
    if (timespan.days) {
        timeString += `${timespan.days}d `;
    }
    if (timespan.hours) {
        timeString += `${timespan.hours}h `;
    }
    if (timespan.minutes) {
        timeString += `${timespan.minutes}m `;
    }
    if (timespan.seconds) {
        timeString += `${timespan.seconds}s `;
    }
    return timeString;
};

const sortValues = (a: SelectionValueTypeAlias, b: SelectionValueTypeAlias, subType: SelectionValuesType): number => {
    switch (subType) {
        case CellSubType.STRING:
            return (a as string).localeCompare(b as string);
        case CellSubType.LONG:
        case CellSubType.DOUBLE:
            return (a as number) - (b as number);
        case CellSubType.TIMESPAN:
            return timeSpanToSeconds(a as TimeSpan) - timeSpanToSeconds(b as TimeSpan);
        case CellSubType.TIME:
            return new Date(a as string).getTime() - new Date(b as string).getTime();
        default:
            return 0;
    }
};

const formatValue = (value: SelectionValueTypeAlias, subType: SelectionValuesType): string => {
    switch (subType) {
        case CellSubType.STRING:
        case CellSubType.LONG:
        case CellSubType.DOUBLE:
            return value.toString();
        case CellSubType.TIMESPAN:
            return convertTimespanToString(value as ITimespan);
        case CellSubType.TIME:
            return formatISOToBasicDateFormat(value as string);
        default:
            return "";
    }
};

const generateDropdownOptions = (selectionValues: SelectionValueTypeAlias[] | undefined, subType: SelectionValuesType): DropdownOptionType[] => {
    if (!selectionValues) {
        return [];
    }

    const options = selectionValues.map((value) => {
        return {label: formatValue(value, subType), value};
    });

    options.sort((a, b) => sortValues(a.value, b.value, subType));

    return options;
};

const generateValue = (value: SelectionValueTypeAlias, subType: SelectionValuesType): string => {
    return formatValue(value, subType);
};

const renderValues = (values: SelectionValueTypeAlias[], separator: string, subType: SelectionValuesType) => {
    values.sort((a, b) => sortValues(a, b, subType));

    return values.map((value: SelectionValueTypeAlias, index: number) => {
        return (
            <React.Fragment key={index}>
                <span className="white-space-normal">{generateValue(value, subType)}</span>
                {index !== values.length - 1 && (separator === "\n" ? <br/> : separator)}
            </React.Fragment>
        );
    });
};

export default function MultiValue(props: IMultiValueProps) {
    const selectionValuesSubType = props.subType ? props.subType : CellSubType.STRING;
    const separator = props.multiValueSeparator ? props.multiValueSeparator : ", ";
    const options = generateDropdownOptions(props.selectionValues, selectionValuesSubType);

    const selectedItems = props.value ? props.value.values.map((value: any) => {
        let option;
        switch (props.subType) {
            case CellSubType.STRING:
            case CellSubType.LONG:
            case CellSubType.DOUBLE:
            case CellSubType.TIME:
                option = options.find(option => option.value === value);
                break;
            case CellSubType.TIMESPAN:
                option = options.find(option => JSON.stringify(normalizeTimespan(option.value as ITimespan)) === JSON.stringify(normalizeTimespan(value)));
                break;
            default:
                break;
        }
        return option ? option.value : null;
    }).filter((item: any) => item !== null) : [];

    return (
        <div>
            {props.isInEditMode ?
                <MultiSelect
                    value={selectedItems}
                    options={options}
                    placeholder="Select"
                    onChange={(e) => props.changeHandler({
                        subType: selectionValuesSubType,
                        values: e.value
                    })}
                    style={{width: "100%"}}
                    filter
                    emptyFilterMessage="No results found"
                />
                :
                <div>
                    {props.value.values?.length > 0 ? renderValues(props.value.values, separator, selectionValuesSubType) : "-"}
                </div>
            }
        </div>
    );
}